// Push Notification Service for PWA
// Delivers native browser notifications so popups arrive even when the user
// is NOT on the app (as long as the device has network access).
import { storage } from '../utils/storage';

class PushNotificationService {
  constructor() {
    this.registration = null;
    this.subscription = null;
  }

  // Check if notifications are supported
  isSupported() {
    return (
      'Notification' in window &&
      'serviceWorker' in navigator &&
      'PushManager' in window
    );
  }

  // Request notification permission
  async requestPermission() {
    if (!this.isSupported()) {
      console.log('Push notifications not supported');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      console.log('Notification permission:', permission);
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  // Fetch the VAPID public key from the backend so the applicationServerKey
  // always matches the server's private key.
  // NOTE: VITE_API_URL already includes the "/api/v1" prefix (same as the
  // axios baseURL in services/api.js), so we append only the path segment here.
  async getVapidPublicKey() {
    try {
      const apiUrl =
        import.meta.env.VITE_API_URL ||
        'https://srv1309580.hstgr.cloud/puffin/api/v1';
      const response = await fetch(`${apiUrl}/push/vapid-public-key`);
      if (!response.ok) return null;
      const data = await response.json();
      return data.publicKey || null;
    } catch (error) {
      console.error('Error fetching VAPID public key:', error);
      return null;
    }
  }

  // Compare the applicationServerKey of an existing subscription (a Uint8Array)
  // with the base64url VAPID public key served by the backend.
  subscriptionKeyMatches(subscription, publicKey) {
    try {
      const raw = subscription?.options?.applicationServerKey;
      if (!raw) return false;
      const bytes = raw instanceof Uint8Array ? raw : new Uint8Array(raw);
      const base64 = btoa(String.fromCharCode(...bytes));
      const base64Url = base64
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
      return base64Url === publicKey;
    } catch (error) {
      return false;
    }
  }

  // Subscribe to push notifications for a specific user
  async subscribe(userId) {
    if (!this.isSupported()) {
      return null;
    }

    try {
      // Get service worker registration
      this.registration = await navigator.serviceWorker.ready;

      const publicKey = await this.getVapidPublicKey();
      if (!publicKey) {
        console.error('Could not obtain VAPID public key - push subscribe aborted');
        return null;
      }

      // Check if already subscribed
      let subscription = await this.registration.pushManager.getSubscription();

      // If an old subscription was created with a different (stale/invalid)
      // VAPID key, drop it and create a fresh one so the server can deliver.
      if (subscription && !this.subscriptionKeyMatches(subscription, publicKey)) {
        await subscription.unsubscribe().catch(() => {});
        subscription = null;
      }

      if (!subscription) {
        // Subscribe to push notifications
        subscription = await this.registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(publicKey),
        });

        console.log('Push subscription created:', subscription);
      }

      this.subscription = subscription;

      // Send subscription to backend (with the real user id)
      if (userId) {
        await this.sendSubscriptionToBackend(userId, subscription);
      }

      return subscription;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      return null;
    }
  }

  // Unsubscribe from push notifications
  // Unsubscribe from push notifications (e.g. on logout / permission revoke).
  // Looks up the live subscription via the SW registration if we don't have
  // one cached in memory (e.g. after a page reload).
  async unsubscribe() {
    if (!this.isSupported()) {
      return;
    }

    let subscription = this.subscription;

    if (!subscription) {
      try {
        const registration =
          this.registration || (await navigator.serviceWorker.ready);
        subscription = await registration.pushManager.getSubscription();
      } catch (error) {
        console.error('Error looking up push subscription:', error);
      }
    }

    if (!subscription) {
      return;
    }

    const endpoint = subscription.endpoint;

    try {
      await subscription.unsubscribe();
      console.log('Unsubscribed from push notifications');
    } catch (error) {
      console.error('Error unsubscribing:', error);
    } finally {
      this.subscription = null;
    }

    // Tell the backend so it stops sending push to this (now-dead) endpoint.
    // Best-effort - failure here must not block logout.
    await this.removeSubscriptionFromBackend(endpoint);
  }

  // Remove a subscription from the backend (e.g. on logout / permission revoke).
  async removeSubscriptionFromBackend(endpoint) {
    if (!endpoint) return;
    try {
      const apiUrl =
        import.meta.env.VITE_API_URL ||
        'https://srv1309580.hstgr.cloud/puffin/api/v1';
      await fetch(`${apiUrl}/push/subscribe/me`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${storage.getItem('token') || ''}`,
        },
        body: JSON.stringify({ endpoint }),
      });
    } catch (error) {
      console.error('Error removing subscription from backend:', error);
    }
  }

  // Send subscription to backend
  // Uses the authenticated /subscribe/me route so the server trusts the user
  // id from the token (no userId spoofing).
  async sendSubscriptionToBackend(userId, subscription) {
    try {
      const apiUrl =
        import.meta.env.VITE_API_URL ||
        'https://srv1309580.hstgr.cloud/puffin/api/v1';
      const response = await fetch(`${apiUrl}/push/subscribe/me`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${storage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          subscription,
          platform: this.detectPlatform(),
          device_name: this.detectDeviceName(),
          device_info: this.detectDeviceInfo(),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send subscription to backend');
      }

      console.log('Subscription sent to backend');
    } catch (error) {
      console.error('Error sending subscription to backend:', error);
    }
  }

  // Detect platform (web / chrome / safari / android / ios ...)
  detectPlatform() {
    try {
      const ua = navigator.userAgent || '';
      const isAndroid = /Android/i.test(ua);
      const isIOS = /iPhone|iPad|iPod/i.test(ua);
      const isMac = /Mac OS X/i.test(ua);
      const isWindows = /Windows/i.test(ua);
      const isLinux = /Linux/i.test(ua);
      const isChrome = /Chrome\//i.test(ua) && !/Edg\//i.test(ua);
      const isSafari = /Safari\//i.test(ua) && !/Chrome\//i.test(ua);
      const isEdge = /Edg\//i.test(ua);
      const isFirefox = /Firefox\//i.test(ua);

      let os = 'web';
      if (isAndroid) os = 'android';
      else if (isIOS) os = 'ios';
      else if (isMac) os = 'macos';
      else if (isWindows) os = 'windows';
      else if (isLinux) os = 'linux';

      if (isAndroid || isIOS) return os;
      if (isChrome) return 'chrome';
      if (isSafari) return 'safari';
      if (isEdge) return 'edge';
      if (isFirefox) return 'firefox';
      return os;
    } catch (error) {
      return 'web';
    }
  }

  // Human readable device name
  detectDeviceName() {
    try {
      const isAndroid = /Android/i.test(navigator.userAgent || '');
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent || '');
      const platform = navigator.platform || '';
      if (isAndroid) return 'Android device';
      if (isIOS) return 'iPhone/iPad';
      return `Desktop (${platform || 'unknown'})`;
    } catch (error) {
      return 'Unknown device';
    }
  }

  // Free-form device metadata
  detectDeviceInfo() {
    try {
      return {
        user_agent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
        is_mobile: /Mobi/i.test(navigator.userAgent || ''),
        standalone: window.matchMedia?.('(display-mode: standalone)')?.matches || false,
      };
    } catch (error) {
      return null;
    }
  }

  // Show local notification
  async showNotification(title, options = {}) {
    if (!this.isSupported() || Notification.permission !== 'granted') {
      console.log('Cannot show notification - permission not granted');
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification(title, {
        body: options.body || '',
        icon: options.icon || '/puffin_icon.png',
        badge: options.badge || '/puffin_icon.png',
        tag: options.tag || 'puffin-notification',
        requireInteraction: options.requireInteraction || false,
        data: options.data || {},
        vibrate: options.vibrate || [300, 80, 300, 80, 300, 80, 300],
        silent: false,
        actions:
          options.actions || [
            { action: 'open', title: 'Open' },
            { action: 'close', title: 'Close' },
          ],
      });

      // Update badge count
      if ('setAppBadge' in navigator) {
        const count = options.badgeCount || 1;
        await navigator.setAppBadge(count);
      }
    } catch (error) {
      console.error('Error showing notification:', error);
    }
  }

  // Clear badge
  async clearBadge() {
    if ('clearAppBadge' in navigator) {
      try {
        await navigator.clearAppBadge();
      } catch (error) {
        console.error('Error clearing badge:', error);
      }
    }
  }

  // Set badge count
  async setBadgeCount(count) {
    if ('setAppBadge' in navigator) {
      try {
        if (count > 0) {
          await navigator.setAppBadge(count);
        } else {
          await navigator.clearAppBadge();
        }
      } catch (error) {
        console.error('Error setting badge:', error);
      }
    }
  }

  // Helper function to convert VAPID key
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}

// Create singleton instance
const pushNotificationService = new PushNotificationService();

export default pushNotificationService;
